import React, { useState, useEffect, KeyboardEvent } from 'react';
import {
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Particles } from '../components/Particles.tsx';
import DashBoardNavBar from '../components/DashBoardNavBar.tsx';
import Navbar from '../components/Navbar.tsx';
import Cookies from 'js-cookie';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

export interface Cell {
  solution: string;
  number?: number;
  black?: boolean;
}

export interface Puzzle {
  id: number;
  grid: Cell[][];
}

const isAuthenticated = !!Cookies.get('token');

const assignClueNumbers = (grid: Cell[][]): Cell[][] => {
  let num = 1;
  const newGrid = grid.map((row) => row.map((cell) => ({ ...cell })));
  for (let r = 0; r < newGrid.length; r++) {
    for (let c = 0; c < newGrid[r].length; c++) {
      const cell = newGrid[r][c];
      if (cell.black) continue;
      const leftBlocked = c === 0 || newGrid[r][c - 1].black;
      const aboveBlocked = r === 0 || newGrid[r - 1][c].black;
      if (leftBlocked || aboveBlocked) {
        cell.number = num++;
      }
    }
  }
  return newGrid;
};

const generateAcrossClues = (grid: Cell[][]) => {
  const clues: { number: number; clue: string; answer: string }[] = [];
  for (let r = 0; r < grid.length; r++) {
    let c = 0;
    while (c < grid[r].length) {
      const cell = grid[r][c];
      if (!cell.black && (c === 0 || grid[r][c - 1].black)) {
        const startNum = cell.number || 0;
        let answer = '';
        let cc = c;
        while (cc < grid[r].length && !grid[r][cc].black) {
          answer += grid[r][cc].solution;
          cc++;
        }
        clues.push({
          number: startNum,
          clue: `Across ${startNum}: Answer is "${answer}"`,
          answer,
        });
        c = cc;
      } else {
        c++;
      }
    }
  }
  return clues;
};

const generateDownClues = (grid: Cell[][]) => {
  const clues: { number: number; clue: string; answer: string }[] = [];
  const numRows = grid.length;
  const numCols = grid[0].length;
  for (let c = 0; c < numCols; c++) {
    let r = 0;
    while (r < numRows) {
      const cell = grid[r][c];
      if (!cell.black && (r === 0 || grid[r - 1][c].black)) {
        const startNum = cell.number || 0;
        let answer = '';
        let rr = r;
        while (rr < numRows && !grid[rr][c].black) {
          answer += grid[rr][c].solution;
          rr++;
        }
        clues.push({
          number: startNum,
          clue: `Down ${startNum}: Answer is "${answer}"`,
          answer,
        });
        r = rr;
      } else {
        r++;
      }
    }
  }
  return clues;
};

const generateEmptyUserGrid = (grid: Cell[][]): string[][] => {
  return grid.map((row) =>
    row.map((cell) => (cell.black ? '#' : ''))
  );
};

const GRID_SIZE = 13;
const SOLUTION_WORD = 'PUZZLEMASTERY';

const generatePuzzle1 = (): Puzzle => {
  const baseWord = 'PUZZLEMASTERY';
  const grid: Cell[][] = [];
  for (let r = 0; r < GRID_SIZE; r++) {
    const row: Cell[] = [];
    for (let c = 0; c < GRID_SIZE; c++) {
      row.push({ solution: baseWord[c] });
    }
    grid.push(row);
  }
  const blacks = [
    { r: 2, c: 5 },
    { r: 2, c: 6 },
    { r: 5, c: 3 },
    { r: 7, c: 10 },
    { r: 10, c: 0 },
    { r: 10, c: 12 },
  ];
  blacks.forEach(({ r, c }) => {
    grid[r][c] = { solution: '', black: true };
  });
  return { id: 1, grid: assignClueNumbers(grid) };
};

const generatePuzzle2 = (): Puzzle => {
  const baseWord = 'CROSSWORDGAME';
  const grid: Cell[][] = [];
  for (let r = 0; r < GRID_SIZE; r++) {
    const row: Cell[] = [];
    for (let c = 0; c < GRID_SIZE; c++) {
      row.push({ solution: baseWord[c] });
    }
    grid.push(row);
  }
  const blacks = [
    { r: 3, c: 4 },
    { r: 4, c: 4 },
    { r: 6, c: 7 },
    { r: 8, c: 0 },
    { r: 8, c: 12 },
  ];
  blacks.forEach(({ r, c }) => {
    grid[r][c] = { solution: '', black: true };
  });
  return { id: 2, grid: assignClueNumbers(grid) };
};

const puzzles: Puzzle[] = [generatePuzzle1(), generatePuzzle2()];

const initialPuzzle: Cell[][] = Array.from({ length: GRID_SIZE }, (_, row) =>
  Array.from({ length: GRID_SIZE }, (_, col) => ({
    solution: SOLUTION_WORD[col],
    number: col === 0 ? row + 1 : undefined,
  }))
);

const createEmptyGrid = (size: number): Cell[][] => {
  return Array.from({ length: size }, () =>
    Array.from({ length: size }, () => ({ solution: '', black: false, number: undefined }))
  );
};

const reassignNumbers = (grid: Cell[][]): Cell[][] => {
  let num = 1;
  return grid.map((row) =>
    row.map((cell) => {
      if (!cell.black && cell.number !== undefined) {
        return { ...cell, number: num++ };
      }
      return cell;
    })
  );
};

const CrosswordPuzzle: React.FC = () => {
  const [puzzleIndex, setPuzzleIndex] = useState(0);
  const currentPuzzle = puzzles[puzzleIndex];
  const [userGrid, setUserGrid] = useState<string[][]>(
    generateEmptyUserGrid(currentPuzzle.grid)
  );
  const [grid, setGrid] = useState<string[][]>(
    Array.from({ length: GRID_SIZE }, () => Array(GRID_SIZE).fill(''))
  );
  const [selected, setSelected] = useState<{ row: number; col: number }>({
    row: 0,
    col: 0,
  });
  const [direction, setDirection] = useState<'across' | 'down'>('across');
  const [errors, setErrors] = useState<boolean[][]>(
    Array.from({ length: GRID_SIZE }, () => Array(GRID_SIZE).fill(false))
  );

  // Custom puzzle állapotok
  const [customDialogOpen, setCustomDialogOpen] = useState(false);
  const [creationStep, setCreationStep] = useState(0); // 0: választás, 1: map méret, 2: puzzle board
  const [creationSelection, setCreationSelection] = useState<'empty' | 'puzzle' | null>(null);
  const [mapSize, setMapSize] = useState(13);
  const [mapSizeError, setMapSizeError] = useState('');
  const [customGrid, setCustomGrid] = useState<Cell[][]>([]);
  const [mapStyle, setMapStyle] = useState<'simple' | 'extra'>('extra');
  const [puzzleTitle, setPuzzleTitle] = useState('My Puzzle');
  const [customAcrossClues, setCustomAcrossClues] = useState<{ id: number; text: string; editing: boolean }[]>([]);
  const [customDownClues, setCustomDownClues] = useState<{ id: number; text: string; editing: boolean }[]>([]);

  // Mód: "dark" vagy "number"
  const [editMode, setEditMode] = useState<'dark' | 'number'>('dark');
  // Következő szám kiszámítása a számozáshoz
  const [nextNumber, setNextNumber] = useState(1);

  // Segédfüggvény, hogy hány cellának van szám értéke
  const countNumbers = (grid: Cell[][]): number =>
    grid.flat().filter((cell) => cell.number !== undefined).length;

  // Ha a felhasználó a custom board cellájára kattint
  const handleCustomCellClick = (rowIndex: number, colIndex: number) => {
    setCustomGrid((prevGrid) => {
      const newGrid = prevGrid.map((row) => row.map((cell) => ({ ...cell })));
      const cell = newGrid[rowIndex][colIndex];
      if (editMode === 'dark') {
        cell.black = true;
        cell.number = undefined;
        const updatedGrid = reassignNumbers(newGrid);
        setNextNumber(countNumbers(updatedGrid) + 1);
        return updatedGrid;
      } else if (editMode === 'number') {
        if (cell.black) return newGrid; // nem számozhatunk fekete cellát
        if (cell.number === undefined) {
          // Új szám hozzárendelése: a számozott cellák száma + 1
          cell.number = countNumbers(newGrid) + 1;
        } else {
          // Ha már van szám, akkor töröljük azt
          cell.number = undefined;
        }
        const updatedGrid = reassignNumbers(newGrid);
        setNextNumber(countNumbers(updatedGrid) + 1);
        return updatedGrid;
      }
      return newGrid;
    });
  };

  const handleClearBoard = () => {
    setCustomGrid(createEmptyGrid(mapSize));
    setNextNumber(1);
  };

  // Print és PDF letöltés kezelése
  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = () => {
    const input = document.getElementById('printable-area');
    if (input) {
      const clone = input.cloneNode(true) as HTMLElement;
      const noPrintElements = clone.getElementsByClassName('no-print');
      
      while (noPrintElements.length > 0) {
        noPrintElements[0].parentNode?.removeChild(noPrintElements[0]);
      }

      clone.style.position = 'fixed';
      clone.style.left = '-9999px';
      document.body.appendChild(clone);

      html2canvas(clone).then((canvas) => {
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF();
        const imgProps = pdf.getImageProperties(imgData);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
        
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
        pdf.save('custom_puzzle.pdf');
        document.body.removeChild(clone);
      });
    }
  };

  const moveSelection = (newRow: number, newCol: number) => {
    if (newRow >= 0 && newRow < GRID_SIZE && newCol >= 0 && newCol < GRID_SIZE) {
      setSelected({ row: newRow, col: newCol });
    }
  };

  useEffect(() => {
    setUserGrid(generateEmptyUserGrid(currentPuzzle.grid));
    setSelected({ row: 0, col: 0 });
    setErrors(Array.from({ length: GRID_SIZE }, () => Array(GRID_SIZE).fill(false)));
  }, [currentPuzzle]);

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const { row, col } = selected;
    const cell = currentPuzzle.grid[row][col];
    if (!cell || cell.black) return;
    if (/^[a-zA-Z]$/.test(e.key)) {
      const letter = e.key.toUpperCase();
      setUserGrid((prev) => {
        const newGrid = prev.map((r) => [...r]);
        newGrid[row][col] = letter;
        return newGrid;
      });
      if (direction === 'across') {
        moveSelection(row, col + 1);
      } else {
        moveSelection(row + 1, col);
      }
      return;
    }
    switch (e.key) {
      case 'ArrowUp':
        moveSelection(row - 1, col);
        break;
      case 'ArrowDown':
        moveSelection(row + 1, col);
        break;
      case 'ArrowLeft':
        moveSelection(row, col - 1);
        break;
      case 'ArrowRight':
        moveSelection(row, col + 1);
        break;
      case 'Backspace': {
        e.preventDefault();
        setUserGrid((prev) => {
          const newGrid = prev.map((r) => [...r]);
          newGrid[row][col] = '';
          return newGrid;
        });
        if (direction === 'across') {
          moveSelection(row, col - 1);
        } else {
          moveSelection(row - 1, col);
        }
        break;
      }
      case ' ':
        e.preventDefault();
        setDirection((prev) => (prev === 'across' ? 'down' : 'across'));
        break;
      default:
        break;
    }
  };

  const solveLetter = () => {
    const { row, col } = selected;
    const cell = currentPuzzle.grid[row][col];
    if (!cell.black) {
      setUserGrid((prev) => {
        const newGrid = prev.map((r) => [...r]);
        newGrid[row][col] = cell.solution;
        return newGrid;
      });
    }
  };

  const solveVerticalWord = () => {
    const { row, col } = selected;
    let start = row;
    while (start > 0 && !currentPuzzle.grid[start - 1][col].black) {
      start--;
    }
    let end = row;
    while (end < GRID_SIZE - 1 && !currentPuzzle.grid[end + 1][col].black) {
      end++;
    }
    setUserGrid((prev) => {
      const newGrid = prev.map((r) => [...r]);
      for (let r = start; r <= end; r++) {
        newGrid[r][col] = currentPuzzle.grid[r][col].solution;
      }
      return newGrid;
    });
  };

  const solveHorizontalWord = () => {
    const { row, col } = selected;
    let start = col;
    while (start > 0 && !currentPuzzle.grid[row][start - 1].black) {
      start--;
    }
    let end = col;
    while (end < GRID_SIZE - 1 && !currentPuzzle.grid[row][end + 1].black) {
      end++;
    }
    setUserGrid((prev) => {
      const newGrid = prev.map((r) => [...r]);
      for (let c = start; c <= end; c++) {
        newGrid[row][c] = currentPuzzle.grid[row][c].solution;
      }
      return newGrid;
    });
  };

  const solvePuzzle = () => {
    setUserGrid(
      currentPuzzle.grid.map((row) =>
        row.map((cell) => (cell.black ? '#' : cell.solution))
      )
    );
  };

  const nextPuzzle = () => {
    const nextIndex = (puzzleIndex + 1) % puzzles.length;
    setPuzzleIndex(nextIndex);
  };

  const [openPuzzleId, setOpenPuzzleId] = useState<string>('1');
  const openPuzzle = () => {
    const id = openPuzzleId ? parseInt(openPuzzleId, 10) : 1;
    const index = puzzles.findIndex((p) => p.id === id);
    if (index !== -1) {
      setPuzzleIndex(index);
    }
  };

  const acrossClues = generateAcrossClues(currentPuzzle.grid);
  const downClues = generateDownClues(currentPuzzle.grid);

  useEffect(() => {
    const el = document.getElementById('crossword-container');
    el?.focus();
  }, [currentPuzzle]);

  const checkSolution = () => {
    const newErrors = grid.map((row, r) =>
      row.map((letter, c) => letter !== initialPuzzle[r][c].solution)
    );
    setErrors(newErrors);
  };

  useEffect(() => {
    const el = document.getElementById('crossword-container');
    el?.focus();
  }, []);

  return (
    <div style={{ position: 'relative', minHeight: '100vh' }}>
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: -1,
        }}
      >
        <Particles />
      </div>

      {isAuthenticated ? <DashBoardNavBar /> : <Navbar />}

      <Box
        id="crossword-container"
        tabIndex={0}
        onKeyDown={handleKeyDown}
        sx={{
          outline: 'none',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <Typography variant="h4" gutterBottom>
          Crossword Puzzle
        </Typography>

        {/* Puzzle Board */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Box sx={{ display: 'inline-block', border: '2px solid #333' }}>
            {userGrid.map((rowData, r) => (
              <Box key={r} sx={{ display: 'flex' }}>
                {rowData.map((cellValue, c) => {
                  const cell = currentPuzzle.grid[r][c];
                  const isSelected = selected.row === r && selected.col === c;
                  return (
                    <Paper
                      key={c}
                      onClick={() => setSelected({ row: r, col: c })}
                      sx={{
                        width: 30,
                        height: 30,
                        border: '1px solid #999',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        backgroundColor: cell.black ? '#000' : '#fff',
                        color: cell.black ? '#fff' : '#000',
                        cursor: cell.black ? 'default' : 'pointer',
                        boxShadow: isSelected ? '0 0 0 2px #1976d2' : 'none',
                        mr: 0.25,
                        mb: 0.25,
                        fontSize: '0.9rem',
                      }}
                    >
                      {!cell.black && cell.number && (
                        <Typography
                          variant="caption"
                          sx={{
                            position: 'absolute',
                            top: 1,
                            left: 1,
                            fontSize: '0.5rem',
                          }}
                        >
                          {cell.number}
                        </Typography>
                      )}
                      {!cell.black && (
                        <Typography variant="body1">
                          {cellValue}
                        </Typography>
                      )}
                    </Paper>
                  );
                })}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Function Buttons */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Button variant="contained" onClick={solveLetter}>
            Solve Letter
          </Button>
          <Button variant="contained" onClick={solveVerticalWord}>
            Solve Vertical Word
          </Button>
          <Button variant="contained" onClick={solveHorizontalWord}>
            Solve Horizontal Word
          </Button>
          <Button variant="contained" onClick={solvePuzzle}>
            Solve Puzzle
          </Button>
          <Button variant="contained" onClick={nextPuzzle}>
            Next Puzzle
          </Button>
          <TextField
            label="Puzzle ID"
            size="small"
            value={openPuzzleId}
            onChange={(e) => setOpenPuzzleId(e.target.value)}
            sx={{ width: 100 }}
          />
          <Button variant="contained" onClick={openPuzzle}>
            Open Puzzle
          </Button>
        </Box>

        <Typography variant="body2" sx={{ mb: 1 }}>
          Current direction: {direction.toUpperCase()} (press SPACE to toggle)
        </Typography>

        <Grid container spacing={2} sx={{ maxWidth: 800 }}>
          <Grid item xs={6}>
            <Typography variant="h6">Across</Typography>
            {acrossClues.map((clue) => (
              <Box key={clue.number} sx={{ my: 0.5 }}>
                <Typography variant="subtitle2">
                  {clue.number}. {clue.clue}
                </Typography>
              </Box>
            ))}
          </Grid>
          <Grid item xs={6}>
            <Typography variant="h6">Down</Typography>
            {downClues.map((clue) => (
              <Box key={clue.number} sx={{ my: 0.5 }}>
                <Typography variant="subtitle2">
                  {clue.number}. {clue.clue}
                </Typography>
              </Box>
            ))}
          </Grid>
        </Grid>

        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            onClick={() => {
              setCustomDialogOpen(true);
              setCreationStep(0);
              setCreationSelection(null);
            }}
          >
            Custom
          </Button>
        </Box>
      </Box>

      {/* 1. Lépés: Választó Dialog */}
      <Dialog open={customDialogOpen && creationStep === 0} onClose={() => setCustomDialogOpen(false)}>
        <DialogTitle>Create Custom Puzzle</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Button
              variant="contained"
              onClick={() => setCreationSelection('empty')}
              color={creationSelection === 'empty' ? 'primary' : 'inherit'}
            >
              Create an empty map
            </Button>
            <Button variant="contained" disabled>
              Create a Puzzle Game
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (creationSelection === 'empty') {
                setCreationStep(1);
              }
            }}
            disabled={!creationSelection}
          >
            NEXT
          </Button>
        </DialogActions>
      </Dialog>

      {/* 2. Lépés: Map Size Dialog */}
      <Dialog open={customDialogOpen && creationStep === 1} onClose={() => setCustomDialogOpen(false)}>
        <DialogTitle>Map Size & Puzzle Settings</DialogTitle>
        <DialogContent>
          <TextField
            label="Map size (1-20; e.g., 13 for 13x13)"
            type="number"
            value={mapSize}
            onChange={(e) => {
              const value = parseInt(e.target.value, 10);
              if (value > 0 && value <= 20) {
                setMapSize(value);
                setMapSizeError('');
              } else {
                setMapSizeError('Please enter a number between 1 and 20');
              }
            }}
            error={!!mapSizeError}
            helperText={mapSizeError}
            fullWidth
            margin="dense"
          />
          {/* Új styled rész: Map Style választás */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1">Select Map Style:</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <input
                type="checkbox"
                checked={mapStyle === 'simple'}
                onChange={(e) => { if(e.target.checked) setMapStyle('simple'); }}
              />
              <Typography>Simple</Typography>
              <input
                type="checkbox"
                checked={mapStyle === 'extra'}
                onChange={(e) => { if(e.target.checked) setMapStyle('extra'); }}
              />
              <Typography>Extra</Typography>
            </Box>
          </Box>
          {/* Puzzle Title input */}
          <TextField
            label="Puzzle Title"
            value={puzzleTitle}
            onChange={(e) => setPuzzleTitle(e.target.value)}
            fullWidth
            margin="dense"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCustomGrid(createEmptyGrid(mapSize));
              setNextNumber(1);
              setEditMode('dark'); // alapértelmezett mód
              setCreationStep(2);
            }}
            disabled={!!mapSizeError || !mapSize}
          >
            NEXT
          </Button>
        </DialogActions>
      </Dialog>


      {/* A stílusok, melyek a nyomtatás/PDF generálás során elrejtik a nem kívánt elemeket */}
      <style>
        {`
          @media print {
            .no-print {
              display: none !important;
            }
          }
        `}
      </style>

      
      {/* 3. Lépés: Custom Puzzle Board Dialog */}
      <Dialog open={customDialogOpen && creationStep === 2} onClose={() => setCustomDialogOpen(false)} maxWidth="lg">
        <DialogTitle sx={{ textAlign: 'center' }}>
          {puzzleTitle} ({mapSize} x {mapSize})
        </DialogTitle>
        <DialogContent>
          {/* A printable terület: középre igazított */}
          <Box id="printable-area" sx={{ textAlign: 'center' }}>
            {/* Puzzle board */}
            <Box
              id="custom-puzzle-board"
              sx={{
                justifyContent: 'center',
                display: mapStyle === 'simple' ? 'grid' : 'grid',
                gridTemplateColumns: `repeat(${mapSize}, 30px)`,
                gap: mapStyle === 'simple' ? '1px' : '2px',
                mt: 2,
                mb: 1,
                border: mapStyle === 'extra' ? '2px solid #333' : 'none',
                p: mapStyle === 'extra' ? 1 : 0,
                margin: '0 auto'
              }}
            >
              {customGrid.map((row, r) =>
                row.map((cell, c) => (
                  <Paper
                    key={`${r}-${c}`}
                    onClick={() => {
                      setCustomGrid((prevGrid) => {
                        const newGrid = prevGrid.map((row) => row.map((cell) => ({ ...cell })));
                        const clickedCell = newGrid[r][c];
                        if (editMode === 'dark') {
                          // Ha már fekete, akkor toggle: fehérre váltás
                          clickedCell.black = !clickedCell.black;
                          if (!clickedCell.black) {
                            clickedCell.number = undefined;
                          }
                        } else if (editMode === 'number') {
                          if (clickedCell.black) return newGrid;
                          if (clickedCell.number === undefined) {
                            clickedCell.number = countNumbers(newGrid) + 1;
                          } else {
                            clickedCell.number = undefined;
                          }
                          const updatedGrid = reassignNumbers(newGrid);
                          setNextNumber(countNumbers(updatedGrid) + 1);
                          return updatedGrid;
                        }
                        const updatedGrid = reassignNumbers(newGrid);
                        setNextNumber(countNumbers(updatedGrid) + 1);
                        return updatedGrid;
                      });
                    }}
                    sx={{
                      width: 30,
                      height: 30,
                      backgroundColor: cell.black ? '#000' : '#fff',
                      color: cell.black ? '#fff' : '#000',
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'flex-start',
                      p: 0.5,
                      fontSize: '0.7rem',
                      cursor: 'pointer',
                      border: '1px solid #999',
                    }}
                  >
                    {cell.number ? cell.number : ''}
                  </Paper>
                ))
              )}
            </Box>
            {/* Clues oszlopok */}
            <Box sx={{ display: 'flex', gap: 2, mt: 2, justifyContent: 'center' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6">Across</Typography>
                {customAcrossClues.map((clue, index) => (
                  <Box key={clue.id} sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <Typography variant="caption" sx={{ width: 30 }}>{index + 1}.</Typography>
                    {clue.editing ? (
                      <>
                        <TextField
                          value={clue.text}
                          onChange={(e) => {
                            setCustomAcrossClues((prev) =>
                              prev.map((c) =>
                                c.id === clue.id ? { ...c, text: e.target.value } : c
                              )
                            );
                          }}
                          size="small"
                        />
                        <Button className="no-print" onClick={() => {
                          setCustomAcrossClues((prev) =>
                            prev.map((c) =>
                              c.id === clue.id ? { ...c, editing: false } : c
                            )
                          );
                        }}>OK</Button>
                        <Button className="no-print" onClick={() => {
                          setCustomAcrossClues((prev) =>
                            prev.filter((c) => c.id !== clue.id)
                          );
                        }}>X</Button>
                      </>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography>{clue.text}</Typography>
                        <Button className="no-print" onClick={() => {
                          setCustomAcrossClues((prev) =>
                            prev.map((c) =>
                              c.id === clue.id ? { ...c, editing: true } : c
                            )
                          );
                        }}>Edit</Button>
                        <Button className="no-print" onClick={() => {
                          setCustomAcrossClues((prev) =>
                            prev.filter((c) => c.id !== clue.id)
                          );
                        }}>X</Button>
                      </Box>
                    )}
                  </Box>
                ))}
                <Button className="no-print" onClick={() => {
                  const newClue = { id: Date.now(), text: '', editing: true };
                  setCustomAcrossClues((prev) => [...prev, newClue]);
                }}>ADD</Button>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6">Down</Typography>
                {customDownClues.map((clue, index) => (
                  <Box key={clue.id} sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <Typography variant="caption" sx={{ width: 30 }}>{index + 1}.</Typography>
                    {clue.editing ? (
                      <>
                        <TextField
                          value={clue.text}
                          onChange={(e) => {
                            setCustomDownClues((prev) =>
                              prev.map((c) =>
                                c.id === clue.id ? { ...c, text: e.target.value } : c
                              )
                            );
                          }}
                          size="small"
                        />
                        <Button className="no-print" onClick={() => {
                          setCustomDownClues((prev) =>
                            prev.map((c) =>
                              c.id === clue.id ? { ...c, editing: false } : c
                            )
                          );
                        }}>OK</Button>
                        <Button className="no-print" onClick={() => {
                          setCustomDownClues((prev) =>
                            prev.filter((c) => c.id !== clue.id)
                          );
                        }}>X</Button>
                      </>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography>{clue.text}</Typography>
                        <Button className="no-print" onClick={() => {
                          setCustomDownClues((prev) =>
                            prev.map((c) =>
                              c.id === clue.id ? { ...c, editing: true } : c
                            )
                          );
                        }}>Edit</Button>
                        <Button className="no-print" onClick={() => {
                          setCustomDownClues((prev) =>
                            prev.filter((c) => c.id !== clue.id)
                          );
                        }}>X</Button>
                      </Box>
                    )}
                  </Box>
                ))}
                <Button className="no-print" onClick={() => {
                  const newClue = { id: Date.now(), text: '', editing: true };
                  setCustomDownClues((prev) => [...prev, newClue]);
                }}>ADD</Button>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => setEditMode(editMode === 'dark' ? 'number' : 'dark')}
          >
            Switch to {editMode === 'dark' ? 'Number' : 'Dark Cell'}
          </Button>
          <Button variant="contained" color="error" onClick={handleClearBoard}>
            Clear Puzzle Board
          </Button>
          {/* Print és PDF letöltés: a printable-area tartalma látszódjon */}
          <Button variant="contained" onClick={handlePrint}>
            Print
          </Button>
          <Button variant="contained" onClick={handleDownloadPdf}>
            Download PDF
          </Button>
        </DialogActions>
      </Dialog>


    </div>
  );
};

export default CrosswordPuzzle;
